/**
 * 63.7 (b), decision 135 B (the owner: "I think B"): the planet-scale buildings sized by function at person scale. The
 * team's building (the ecotourism centre, the shield, the landing pad — drawn as its owner team's builder's model) and
 * the antenna (its listening post) take their bodies from their own models at person scale (constants/hitboxes.ts
 * BUILDING_HITBOXES, in people), the same on every planet, where the clock plays them so (ClockPolicy.buildings) at a
 * person scale (k < 1: at classic scale people and buildings share the planet's family). Elsewhere — the turn game under
 * any profile, the classic scale — the shared planet-scaled row as before.
 *
 * One answer for every reader: the bodies' shapes (core/hits.ts: bullets, sight, blasts by shape), their walk obstacles
 * and cover (core/surfaceWalk.ts), and the drawing (rendering/renderer.ts). The ruins are bodies on the same terms
 * (`ruinHitbox`): their person-scale models as they stand; elsewhere a ruin is no body, as ever.
 */
import type { GameConfig, GameState, RuinState, StructureState, StructureType, Vec3 } from '../types';
import type { HitboxDef } from '../types/body';
import { BODIES } from '../constants/body';
import { BUILDING_HITBOXES, BUILDING_RUIN_HEIGHT, FOOTHOLD_HITBOXES, DROP_CONTAINER_OPEN, SHIP_WRECK_PIECES, POINT_DEFENCE_HITBOXES } from '../constants/hitboxes';
import { clockPolicyFor } from '../match/clockPolicy';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { surfacePropScale } from './propScale';
import { layoutFor } from './modules';
import { layoutHitbox, layoutRuin, shutOf } from './moduleShapes';
import { arcDistance } from './gameState';
import { vecAdd, vecScale, vecNormalize, surfaceTangentFrame } from '../math';
import { dcos, dsin } from './dmath';

type SizingConfig = Pick<GameConfig, 'clock' | 'scale' | 'personTuning'> & Partial<Pick<GameConfig, 'modularBuildings'>>;

/** The team builders (rendering/buildingModels' BUILDERS; BUILDING_PERSON_SCALE.team, in order). */
export const BUILDERS = BUILDING_HITBOXES.team.length;

/** The planet-scale buildings are sized by function: the clock plays them so, at a person scale. */
export function buildingsByFunction(config: SizingConfig): boolean {
  return clockPolicyFor(config).buildings === 'function' && personSetOf({ config }).k < 1;
}

/** A planet-scale building: a kind whose body is the shared planet-scaled row (the team's building, the antenna). */
export function isPlanetBuilding(type: StructureType): boolean {
  return BODIES[type].hitbox.ruler === 'prop';
}

/** A team's builder: whose building it puts up (team index mod BUILDERS). */
export function builderOf(teamIndex: number): number {
  return ((teamIndex % BUILDERS) + BUILDERS) % BUILDERS;
}

/** The hit shape of a structure's body: a planet-scale building sized by function has its own model's; else its type's. */
export function structureHitbox(s: Pick<StructureState, 'type' | 'builder'> & Partial<Pick<StructureState, 'health' | 'shut' | 'modules' | 'rubble' | 'rampOpen' | 'wreck' | 'pd'>>, config: SizingConfig | undefined): HitboxDef {
  if (s.type === 'dropContainer' && s.rampOpen) return DROP_CONTAINER_OPEN;   // 69.3b's follow-up: the ramp down — the walls round an open back
  if (s.type === 'shipWreck' && s.wreck && s.wreck.piece !== 'whole') return SHIP_WRECK_PIECES[s.wreck.piece];   // 69.3b, the ship's crash: a piece of the hull
  if (s.type === 'pointDefence' && s.pd) return POINT_DEFENCE_HITBOXES[s.pd.variant];   // 69.9: the C-RAM's or the CIWS's
  const def = BODIES[s.type].hitbox;
  if (def.ruler !== 'prop' || !config || !buildingsByFunction(config)) return def;
  if (s.type === 'ecotourism') return FOOTHOLD_HITBOXES.hq;   // 67.62: the HQ in the D style (orbital-design-2's)
  if (s.type === 'landingPad') return FOOTHOLD_HITBOXES.landingPad;   // (the pad: its marshaller's post; the matting is a floor)
  const layout = layoutFor(s, config);   // 63.14 step 3: modular buildings on — the builder's layout as modules (core/moduleShapes.ts)
  if (layout && layoutRuined(s)) { const r = layoutRuin(layout); return layoutHitbox(layout, NONE_SHUT, r.modules, r.rubble); }   // 72.1: the ruin's heaps
  if (layout) return layoutHitbox(layout, shutOf(s, layout), s.modules, s.rubble);   // (step 4: the destroyed modules' shapes gone, their rubble in)
  return s.type === 'antenna' ? BUILDING_HITBOXES.post : BUILDING_HITBOXES.team[builderOf(s.builder ?? 0)];
}

const NONE_SHUT: ReadonlySet<number> = new Set();
/** 72.1: a modular building at 0 is its layout's ruin (core/moduleShapes.ts layoutRuin); the motor pool's compound keeps its own (63.16 (7)). */
function layoutRuined(s: Pick<StructureState, 'type'> & Partial<Pick<StructureState, 'health'>>): boolean {
  return s.health !== undefined && s.health <= 0 && s.type !== 'motorPool';
}

/** The share of its height a destroyed structure's rubble stands: a planet-scale building sized by function its own ruin's; else its type's. */
export function structureRuinHeight(s: Pick<StructureState, 'type' | 'builder'>, config: SizingConfig | undefined): number {
  const own = BODIES[s.type].ruinHeight ?? 1;
  if (s.type === 'antenna' || !isPlanetBuilding(s.type) || !config || !buildingsByFunction(config)) return own;
  if (s.type !== 'motorPool' && layoutFor(s, config)) return 1;   // 72.1: a layout's ruin is its heaps, at their own height
  return BUILDING_RUIN_HEIGHT[builderOf(s.builder ?? 0)];
}

/** 63.7 (b)'s follow-up: a ruin's body — its person-scale model as it stands (in people) — where the buildings are sized by function. */
export function ruinHitbox(r: Pick<RuinState, 'ruinType'>): HitboxDef {
  return BUILDING_HITBOXES.ruin[r.ruinType];
}

/** A shape's extent in its ruler: the half width of the square round its parts (its walk obstacle's disc) and its top. */
export function hitboxExtent(def: HitboxDef): { half: number; height: number } {
  let half = 0, height = 0;
  for (const p of def.parts) {
    if (p.shape === 'box') {
      half = Math.max(half, Math.abs(p.centre[0]) + p.half[0], Math.abs(p.centre[2]) + p.half[2]);
      height = Math.max(height, p.centre[1] + p.half[1]);
    } else {
      half = Math.max(half, Math.abs(p.a[0]) + p.radius, Math.abs(p.a[2]) + p.radius, Math.abs(p.b[0]) + p.radius, Math.abs(p.b[2]) + p.radius);
      height = Math.max(height, p.a[1] + p.radius, p.b[1] + p.radius);
    }
  }
  return { half, height };
}

/**
 * The state with each planet-scale building's builder set — its owner's team's (nobody's: the first) — where the
 * buildings are sized by function; else the state as it is (the turn game's states keep their shape). A building that
 * already names its builder keeps it.
 */
export function withBuilders<S extends Pick<GameState, 'config' | 'structures' | 'players'>>(state: S): S {
  if (!buildingsByFunction(state.config) || !state.structures.some(s => s.builder === undefined && isPlanetBuilding(s.type))) return state;
  const teamOf = new Map(state.players.map(p => [p.id, p.teamIndex]));
  return { ...state, structures: state.structures.map(s => s.builder !== undefined || !isPlanetBuilding(s.type) ? s : { ...s, builder: builderOf(teamOf.get(s.ownerId) ?? 0) }) };
}

/** A structure's footprint on the ground (world units): the half width of its shape (its ruler's scale), for clearances. */
export function structureFootprint(s: Pick<StructureState, 'type' | 'builder'>, config: SizingConfig, planetRadius: number): number {
  const def = structureHitbox(s, config), ps = personSetOf({ config });
  const scale = def.ruler === 'prop' ? surfacePropScale(planetRadius) : def.ruler === 'unit' ? PERSON_BODY_HEIGHT : PERSON_BODY_HEIGHT * ps.k;
  return hitboxExtent(def).half * scale;
}

/**
 * What the setup's last pass settles: the planet-scale buildings, and — 65.22 — the emplaced HMG's nest, and — 63.1d —
 * the parked 6×6 (its crate, laid after it, beside it), each by its measured footprint.
 */
const settles = (type: StructureState['type']): boolean => isPlanetBuilding(type) || type === 'hmg' || type === 'wheeled' || type === 'tracked' || type === 'tank' || type === 'technical' || type === 'supplyTruck';   // (63.1o: parked with the 6×6s)   // (69.4 step 5: and the supply truck)

/** The room a building keeps from what it would stand on or block (people): a walk between it and its neighbour. */
export const BUILDING_CLEARANCE = 2;

/**
 * The coordinator, on lane B's every-switch run (seed every-sk-2: an HQ stood on a capture point, and the bots could not
 * reach it; and lane E's diorama, a classic-scale skirmish, the four Lab buildings inside each other): on the action clock,
 * at any scale (the person-scale buildings sized by function, the classic ones planet-scaled), each structure it settles (a
 * planet-scale building, an HMG nest) is moved off what it stands on — a
 * capture point's circle, a unit's spawn, a parked vehicle or any other structure, and the buildings settled before it —
 * by its measured footprint and BUILDING_CLEARANCE. One that stands clear stays; one that doesn't takes the first clear
 * spot on rings round where it was put (two people apart, sixteen spots a ring, out to forty people). Run once, at the
 * end of the setup. The turn game's placement is as it was (its stored cases stand buildings).
 */
export function settleBuildings<S extends Pick<GameState, 'config' | 'structures' | 'players' | 'planets' | 'modeState'>>(state: S): S {
  if (clockPolicyFor(state.config).buildings !== 'function' || !state.structures.some(s => settles(s.type))) return state;
  const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k, margin = BUILDING_CLEARANCE * person;
  let structures = state.structures;
  const settled = new Set<number>();
  for (let i = 0; i < structures.length; i++) {
    const b = structures[i];
    if (!settles(b.type)) continue;
    const planet = state.planets[b.planetIndex];
    if (!planet) continue;
    const R = planet.radius, f = structureFootprint(b, state.config, R);
    // what it keeps clear of: (a direction, the room it needs from there)
    const keep: Array<[Vec3, number]> = [];
    for (const site of state.modeState.points?.sites ?? []) if (site.planetIndex === b.planetIndex) keep.push([site.surfaceDir, ps.captureRadius + f + margin]);
    for (const p of state.players) if (p.planetIndex === b.planetIndex) keep.push([p.surfaceDir, ps.unitRadius + f + margin]);
    for (const o of structures) {
      if (o.id === b.id || o.planetIndex !== b.planetIndex || (settles(o.type) && !settled.has(o.id))) continue;
      keep.push([o.surfaceDir, structureFootprint(o, state.config, R) + f + margin]);
    }
    const clear = (d: Vec3) => keep.every(([at, room]) => arcDistance(d, at, R) >= room);
    settled.add(b.id);
    if (clear(b.surfaceDir)) continue;
    const n = vecNormalize(b.surfaceDir), { tx, ty } = surfaceTangentFrame(n);
    let spot: Vec3 | null = null;
    for (let ring = 1; ring <= 20 && !spot; ring++) {
      const a = (ring * 2 * person) / R;
      for (let k = 0; k < 16 && !spot; k++) {
        const ang = (k / 16) * Math.PI * 2;
        const d = vecNormalize(vecAdd(vecScale(n, dcos(a)), vecScale(vecAdd(vecScale(tx, dcos(ang)), vecScale(ty, dsin(ang))), dsin(a))));
        if (clear(d)) spot = d;
      }
    }
    if (spot) structures = structures.map((x, j) => (j === i ? { ...x, surfaceDir: spot! } : x));
  }
  return structures === state.structures ? state : { ...state, structures };
}

